import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
import { ReactComponent as KeyIcon } from '../../assets/icons/key.svg';
import { ReactComponent as CrossIcon } from '../../assets/icons/cross.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete.svg';
import Button from '../Button/Button';
import TableauDeBord from '../TableauDeBord/TableauDeBord';
import SearchBar from '../SearchBar/SearchBar';
import { isPrestataireLibreRole, isAdminRole, convertUiRoleToApiRole } from '../../utils/roleUtils';
import DeleteMemberModal, { DeleteMemberInfo } from '../DeleteMemberModal/DeleteMemberModal';
import EditUserModal from '../EditUserModal/EditUserModal';
import './GestionComptesPrestataires.css';

const formatPhone = (phone?: string | null): string => {
  if (!phone) return '-';
  const digits = phone.replace(/\D/g, '');
  let n = digits;
  if (digits.startsWith('33') && digits.length === 11) n = digits.slice(2);
  else if (digits.startsWith('0') && digits.length === 10) n = digits.slice(1);
  else return phone;
  return `+33 ${n[0]} ${n.slice(1, 3)} ${n.slice(3, 5)} ${n.slice(5, 7)} ${n.slice(7, 9)}`;
};

const formatEmail = (email?: string | null): string => email ? email.toLowerCase() : '-';

const ROLE_DISPLAY_RANK: Record<string, number> = {
  'Acheteur signataire admin': 3,
  'Prestataire signataire admin': 3,
  'Prestataire libre admin': 3,
  'Acheteur signataire': 2,
  'Prestataire signataire': 2,
  'Acheteur': 1,
  'Prestataire': 1,
  'Prestataire libre': 1,
};

const parseLocalDate = (dateStr: string): number => {
  const parts = (dateStr || '').split('/');
  if (parts.length === 3) return new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0])).getTime();
  return 0;
};

const sortMembersByRoleThenDate = <T extends { role: string; dateMiseAJour: string }>(users: T[]): T[] =>
  [...users].sort((a, b) => {
    const rankDiff = (ROLE_DISPLAY_RANK[b.role] ?? 1) - (ROLE_DISPLAY_RANK[a.role] ?? 1);
    if (rankDiff !== 0) return rankDiff;
    return parseLocalDate(b.dateMiseAJour) - parseLocalDate(a.dateMiseAJour);
  });

interface PrestataireUser {
  id: number | string;
  idUser: number | string;
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  fonction: string;
  role: string;
  dateMiseAJour: string;
  loginEmail?: string;
}

// ── Constante de bascule vue ────────────────────────────────────────
// Changer 'cards' en 'table' pour revenir à l'affichage tableau classique
const MEMBRES_VIEW_MODE: 'cards' | 'table' = 'cards';

const GestionComptesPrestataires: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userData, setUserData] = useState<PrestataireUser[]>([]);
  const [filteredData, setFilteredData] = useState<PrestataireUser[]>([]);
  const [entiteId, setEntiteId] = useState<string | number>('');

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [isResendInvitationModalOpen, setIsResendInvitationModalOpen] = useState(false);
  const [isConfirmUpdateModalOpen, setIsConfirmUpdateModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [createdUserEmail, setCreatedUserEmail] = useState('');
  const [selectedUser, setSelectedUser] = useState<PrestataireUser | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<DeleteMemberInfo | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    fonction: ''
  });
  const [formErrors, setFormErrors] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formApiError, setFormApiError] = useState('');

  // Déterminer si c'est un prestataire libre
  const isPrestataireLibre = isPrestataireLibreRole(user?.role || '');

  // Charger les données des prestataires
  useEffect(() => {
    const fetchPrestataireUsers = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error("Token d'authentification non trouvé");

        // ===== PRESTATAIRE LIBRE =====
        if (isPrestataireLibre) {
          console.log('GestionComptesPrestataires - Mode Prestataire Libre');

          // Utiliser la route /profile/team pour les prestataires libres
          const teamResponse = await fetch(`${process.env.REACT_APP_API_URL}/profile/team`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          if (!teamResponse.ok) {
            const errorData = await teamResponse.json().catch(() => ({}));
            throw new Error(errorData.message || 'Erreur lors de la récupération de l\'équipe');
          }

          const teamData = await teamResponse.json();

          if (teamData.status !== 'success' || !teamData.data?.membres) {
            throw new Error('Format de réponse API inattendu');
          }

          // Transformer les données
          const prestataireUsers: PrestataireUser[] = teamData.data.membres.map((membre: any) => ({
            id: membre.id,
            idUser: membre.user_id,
            nom: membre.nom,
            prenom: membre.prenom,
            email: membre.email,
            telephone: membre.telephone || '',
            fonction: membre.fonction || '',
            role: membre.is_admin ? 'Prestataire libre admin' : 'Prestataire libre',
            loginEmail: membre.email,
            dateMiseAJour: membre.updated_at
              ? new Date(membre.updated_at).toLocaleDateString('fr-FR')
              : '-'
          }));

          setUserData(sortMembersByRoleThenDate(prestataireUsers));
          setFilteredData(sortMembersByRoleThenDate(prestataireUsers));
          return;
        }

        // ===== PRESTATAIRE ACCORD CADRE =====
        console.log('GestionComptesPrestataires - Mode Prestataire Accord Cadre');

        // Récupérer l'ID de l'entité de l'utilisateur connecté
        const userEndpoint = getUserEndpoint();
        if (!userEndpoint) {
          throw new Error('Type de rôle non supporté');
        }

        const meResponse = await fetch(`${process.env.REACT_APP_API_URL}/${userEndpoint}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!meResponse.ok) {
          throw new Error('Erreur lors de la récupération des informations utilisateur');
        }

        const meData = await meResponse.json();
        const entiteIdFromApi = meData.data?.id_entite;

        if (!entiteIdFromApi) {
          throw new Error('ID d\'entité non trouvé');
        }

        setEntiteId(entiteIdFromApi);

        // Récupérer les informations de l'entité avec tous ses utilisateurs
        const entiteResponse = await fetch(`${process.env.REACT_APP_API_URL}/entites/${entiteIdFromApi}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!entiteResponse.ok) {
          throw new Error('Erreur lors de la récupération des utilisateurs');
        }

        const entiteData = await entiteResponse.json();

        if (entiteData.status !== 'success' || !entiteData.data) {
          throw new Error('Format de réponse API inattendu');
        }

        // Transformer les données en liste de TOUS les utilisateurs (admin, signataire, prestataire)
        const prestataireUsers: PrestataireUser[] = [];
        // Un utilisateur promu gestionnaire peut conserver une ligne interlocuteur (ancre de ses
        // lots contrat) : on le dedoublonne pour ne l'afficher qu'une fois, sous son role courant.
        const seenUserIds = new Set<number>();

        // Ajouter les gestionnaires (Prestataire signataire admin) - en lecture seule
        if (entiteData.data.gestionnaires && entiteData.data.gestionnaires.length > 0) {
          entiteData.data.gestionnaires.forEach((gestionnaire: any) => {
            prestataireUsers.push({
              id: gestionnaire.id,
              idUser: gestionnaire.id_user,
              nom: gestionnaire.nom,
              prenom: gestionnaire.prenom,
              email: gestionnaire.email_contact,
              telephone: gestionnaire.telephone || '',
              fonction: gestionnaire.fonction || '',
              role: 'Prestataire signataire admin',
              loginEmail: gestionnaire.user?.email || '',
              dateMiseAJour: new Date(gestionnaire.updated_at || gestionnaire.created_at).toLocaleDateString('fr-FR')
            });
            seenUserIds.add(gestionnaire.id_user);
          });
        }

        // Ajouter les signataires (Prestataire signataire) - en lecture seule
        if (entiteData.data.signataires && entiteData.data.signataires.length > 0) {
          entiteData.data.signataires.forEach((signataire: any) => {
            prestataireUsers.push({
              id: signataire.id,
              idUser: signataire.id_user,
              nom: signataire.nom,
              prenom: signataire.prenom,
              email: signataire.email_contact,
              telephone: signataire.telephone || '',
              fonction: signataire.fonction || '',
              role: 'Prestataire signataire',
              loginEmail: signataire.user?.email || '',
              dateMiseAJour: new Date(signataire.updated_at || signataire.created_at).toLocaleDateString('fr-FR')
            });
            seenUserIds.add(signataire.id_user);
          });
        }

        // Ajouter les interlocuteurs (Prestataire simples) - modifiables
        if (entiteData.data.interlocuteurs && entiteData.data.interlocuteurs.length > 0) {
          entiteData.data.interlocuteurs.forEach((interlocuteur: any) => {
            // Ne pas afficher deux fois une personne deja listee comme gestionnaire/signataire
            // (cas du profil interlocuteur conserve comme ancre des lots contrat apres promotion).
            if (seenUserIds.has(interlocuteur.id_user)) {
              return;
            }
            prestataireUsers.push({
              id: interlocuteur.id,
              idUser: interlocuteur.id_user,
              nom: interlocuteur.nom,
              prenom: interlocuteur.prenom,
              email: interlocuteur.email_contact,
              telephone: interlocuteur.telephone || '',
              fonction: interlocuteur.fonction || '',
              role: 'Prestataire',
              loginEmail: interlocuteur.user?.email || '',
              dateMiseAJour: new Date(interlocuteur.updated_at || interlocuteur.created_at).toLocaleDateString('fr-FR')
            });
          });
        }

        setUserData(sortMembersByRoleThenDate(prestataireUsers));
        setFilteredData(sortMembersByRoleThenDate(prestataireUsers));

      } catch (error) {
        console.error('Erreur lors de la récupération des utilisateurs:', error);
        setError(error instanceof Error ? error.message : 'Une erreur est survenue');
      } finally {
        setLoading(false);
      }
    };

    if (user?.role) {
      fetchPrestataireUsers();
    }
  }, [user?.role, isPrestataireLibre]);

  // Déterminer le endpoint API en fonction du rôle
  const getUserEndpoint = () => {
    const role = user?.role || '';
    switch (role) {
      case 'Prestataire signataire admin':
        return 'gestionnaires/me';
      case 'Prestataire signataire':
        return 'signataires/me';
      case 'Prestataire':
        return 'interlocuteurs/me';
      default:
        return '';
    }
  };

  // Validation email
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validation téléphone
  const validatePhone = (phone: string): boolean => {
    return /^0[67]\d{8}$/.test(phone.replace(/\s+/g, ''));
  };

  // Validation nom/prénom : lettres (accents inclus), espaces, tirets, apostrophes
  const validateNomPrenom = (value: string): boolean => {
    return /^[\p{L}\s\-']{1,100}$/u.test(value.trim());
  };

  // Sanitize à la saisie : strip les caractères HTML/XSS
  const sanitizeTextInput = (value: string): string => {
    return value.replace(/[<>&"]/g, '').slice(0, 100);
  };

  // Ouvrir le modal de création
  const handleOpenCreateModal = () => {
    setFormData({
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      fonction: ''
    });
    setFormErrors({ nom: '', prenom: '', email: '', telephone: '' });
    setFormApiError('');
    setIsSubmitting(false);
    setIsCreateModalOpen(true);
  };

  // Ouvrir le modal d'édition
  const handleOpenEditModal = (userToEdit: PrestataireUser) => {
    setSelectedUser(userToEdit);
    setIsEditModalOpen(true);
  };

  // Créer un utilisateur
  const handleCreateUser = async () => {
    let hasErrors = false;
    const errors = { nom: '', prenom: '', email: '', telephone: '' };

    if (!formData.nom.trim()) {
      errors.nom = 'Le nom est requis';
      hasErrors = true;
    }

    if (!formData.prenom.trim()) {
      errors.prenom = 'Le prénom est requis';
      hasErrors = true;
    }

    if (!validateEmail(formData.email)) {
      errors.email = 'Email invalide';
      hasErrors = true;
    }

    if (!formData.telephone.trim()) {
      errors.telephone = 'Le téléphone est requis';
      hasErrors = true;
    } else if (!validatePhone(formData.telephone)) {
      errors.telephone = 'Le numéro doit être un mobile français valide (06 ou 07, 10 chiffres)';
      hasErrors = true;
    }

    setFormErrors(errors);
    setFormApiError('');

    if (hasErrors) return;

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');

      const flattenErrors = (errorsObj: Record<string, any>): Record<string, string> => {
        const flat: Record<string, string> = {};
        Object.entries(errorsObj).forEach(([key, msgs]) => {
          if (Array.isArray(msgs) && (msgs as string[]).length > 0) flat[key] = (msgs as string[])[0];
        });
        return flat;
      };

      // ===== PRESTATAIRE LIBRE =====
      if (isPrestataireLibre) {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/profile/team`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            nom: formData.nom,
            prenom: formData.prenom,
            email: formData.email.toLowerCase(),
            telephone: formData.telephone || null,
            fonction: formData.fonction || null,
          }),
        });

        const data = await response.json();
        if (response.status === 422 && data.errors) {
          setFormErrors(prev => ({ ...prev, ...flattenErrors(data.errors) }));
          return;
        }
        if (!response.ok || data.status !== 'success') {
          setFormApiError(data.message || "Erreur lors de la création du membre");
          return;
        }

        const newUser: PrestataireUser = {
          id: data.data?.id || Date.now(),
          idUser: data.data?.user_id || 0,
          nom: formData.nom,
          prenom: formData.prenom,
          email: formData.email.toLowerCase(),
          telephone: formData.telephone || '',
          fonction: formData.fonction || '',
          role: 'Prestataire libre',
          dateMiseAJour: new Date().toLocaleDateString('fr-FR')
        };
        const afterCreate = [...userData, newUser];
        setUserData(afterCreate);
        setFilteredData(afterCreate);
        setIsCreateModalOpen(false);
        setCreatedUserEmail(formData.email.toLowerCase());
        setIsSuccessModalOpen(true);
        return;
      }

      // ===== PRESTATAIRE ACCORD CADRE =====
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/gestionnaires/team`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            nom:       formData.nom,
            prenom:    formData.prenom,
            email:     formData.email.toLowerCase(),
            telephone: formData.telephone || null,
            fonction:  formData.fonction || null,
          }),
        }
      );

      const data = await response.json();
      if (response.status === 422 && data.errors) {
        setFormErrors(prev => ({ ...prev, ...flattenErrors(data.errors) }));
        return;
      }
      if (!response.ok || data.status !== 'success') {
        setFormApiError(data.message || 'Erreur lors de la création du compte');
        return;
      }

      const newUserAC: PrestataireUser = {
        id:            data.data?.id || Date.now(),
        idUser:        data.data?.user_id || 0,
        nom:           formData.nom,
        prenom:        formData.prenom,
        email:         formData.email.toLowerCase(),
        telephone:     formData.telephone || '',
        fonction:      formData.fonction || '',
        role:          'Interlocuteur',
        dateMiseAJour: new Date().toLocaleDateString('fr-FR'),
      };
      const afterCreateAC = [...userData, newUserAC];
      setUserData(afterCreateAC);
      setFilteredData(afterCreateAC);
      setIsCreateModalOpen(false);
      setCreatedUserEmail(formData.email.toLowerCase());
      setIsSuccessModalOpen(true);

    } catch (error) {
      setFormApiError(error instanceof Error ? error.message : 'Une erreur est survenue');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Sauvegarder les modifications d'un membre via EditUserModal
  const handleSavePrestUser = async (userId: string, updatedData: { nom: string; prenom: string; email: string; telephone: string; fonction: string; role?: string }) => {
    if (!selectedUser) return;
    try {
      const token = localStorage.getItem('token');
      let effectiveId = userId;

      if (isPrestataireLibre) {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/profile/team/${userId}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ nom: updatedData.nom, prenom: updatedData.prenom, email: updatedData.email.toLowerCase(), telephone: updatedData.telephone || null, fonction: updatedData.fonction || null })
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Erreur lors de la mise à jour');
        }
        if (updatedData.role && updatedData.role !== selectedUser.role) {
          const newApiRole = convertUiRoleToApiRole(updatedData.role);
          const roleResp = await fetch(`${process.env.REACT_APP_API_URL}/profile/team/${userId}/change-role`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ new_role: newApiRole, type_compte: 'prestataire_libre' })
          });
          if (roleResp.ok) {
            const roleData = await roleResp.json();
            effectiveId = String(roleData.new_id ?? userId);
          }
        }
      } else {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/gestionnaires/team/${userId}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ nom: updatedData.nom, prenom: updatedData.prenom, email_contact: updatedData.email.toLowerCase(), telephone: updatedData.telephone, fonction: updatedData.fonction })
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Erreur lors de la mise à jour');
        }
        if (updatedData.role && updatedData.role !== selectedUser.role) {
          const newApiRole = convertUiRoleToApiRole(updatedData.role);
          const currentApiRole = convertUiRoleToApiRole(selectedUser.role);
          const roleResp = await fetch(`${process.env.REACT_APP_API_URL}/gestionnaires/team/${userId}/change-role`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ new_role: newApiRole, type_compte: currentApiRole })
          });
          if (!roleResp.ok) {
            const roleErr = await roleResp.json().catch(() => ({}));
            throw new Error(roleErr.message || 'Erreur lors du changement de rôle');
          }
          const roleData = await roleResp.json();
          effectiveId = String(roleData.new_id ?? userId);
        }
      }

      const newRole = updatedData.role || selectedUser.role;
      const updatedList = userData.map(u =>
        String(u.id) === String(userId)
          ? { ...u, id: effectiveId, nom: updatedData.nom, prenom: updatedData.prenom, email: updatedData.email.toLowerCase(), telephone: updatedData.telephone || '', fonction: updatedData.fonction || '', role: newRole, dateMiseAJour: new Date().toLocaleDateString('fr-FR') }
          : u
      );
      const sortedList = sortMembersByRoleThenDate(updatedList);
      setUserData(sortedList);
      setFilteredData(sortedList);
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert(`Erreur: ${error instanceof Error ? error.message : 'Une erreur est survenue'}`);
    }
  };

  // Sauvegarder l'email de connexion d'un membre
  const handleSavePrestLoginEmail = async (newEmail: string, memberId: number): Promise<void> => {
    const token = localStorage.getItem('token');
    const prefix = isPrestataireLibre ? 'profile' : 'gestionnaires';
    const response = await fetch(`${process.env.REACT_APP_API_URL}/${prefix}/team/${memberId}/email`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ email: newEmail.toLowerCase() })
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Erreur lors de la mise à jour de l\'email de connexion');
    }
    const updatedList = userData.map(u =>
      Number(u.id) === memberId ? { ...u, loginEmail: newEmail.toLowerCase() } : u
    );
    setUserData(updatedList);
    setFilteredData(updatedList);
  };

  // Obtenir les rôles disponibles selon le contexte
  const getAvailableRolesForUser = (targetUser: PrestataireUser): { value: string; label: string }[] => {
    if (Number(targetUser.idUser) === Number(user?.id)) return [];
    if (!isAdminRole(user?.role || '')) return [];
    if (isPrestataireLibre) {
      return [
        { value: 'Prestataire libre', label: 'Prestataire libre' },
        { value: 'Prestataire libre admin', label: 'Prestataire libre admin' }
      ];
    }
    return [
      { value: 'Prestataire', label: 'Prestataire' },
      { value: 'Prestataire signataire admin', label: 'Prestataire signataire admin' }
    ];
  };

  // Ouvrir le modal de confirmation avant modification
  const handleConfirmUpdate = () => {
    // Validation
    let hasErrors = false;
    const errors = { nom: '', prenom: '', email: '', telephone: '' };

    if (!formData.nom.trim()) {
      errors.nom = 'Le nom est requis';
      hasErrors = true;
    }

    if (!formData.prenom.trim()) {
      errors.prenom = 'Le prénom est requis';
      hasErrors = true;
    }

    if (!validateEmail(formData.email)) {
      errors.email = 'Email invalide';
      hasErrors = true;
    }

    if (!formData.telephone.trim()) {
      errors.telephone = 'Le téléphone est requis';
      hasErrors = true;
    } else if (!validatePhone(formData.telephone)) {
      errors.telephone = 'Le numéro doit être un mobile français valide (06 ou 07, 10 chiffres)';
      hasErrors = true;
    }

    if (hasErrors) {
      setFormErrors(errors);
      return;
    }

    // Si pas d'erreur, ouvrir le modal de confirmation
    setIsConfirmUpdateModalOpen(true);
  };

  // Modifier un utilisateur après confirmation
  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');

      // ===== PRESTATAIRE LIBRE =====
      if (isPrestataireLibre) {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/profile/team/${selectedUser.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            nom: formData.nom,
            prenom: formData.prenom,
            email: formData.email.toLowerCase(),
            telephone: formData.telephone || null,
            fonction: formData.fonction || null
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Erreur lors de la mise à jour');
        }

        const updatedPL = userData.map(u =>
          u.id === selectedUser.id
            ? { ...u, nom: formData.nom, prenom: formData.prenom, email: formData.email.toLowerCase(), telephone: formData.telephone || '', fonction: formData.fonction || '', dateMiseAJour: new Date().toLocaleDateString('fr-FR') }
            : u
        );
        setUserData(updatedPL);
        setFilteredData(updatedPL);
        setIsConfirmUpdateModalOpen(false);
        setIsEditModalOpen(false);
        return;
      }

      // ===== PRESTATAIRE ACCORD CADRE =====
      const response = await fetch(`${process.env.REACT_APP_API_URL}/gestionnaires/team/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          nom: formData.nom,
          prenom: formData.prenom,
          email_contact: formData.email.toLowerCase(),
          telephone: formData.telephone,
          fonction: formData.fonction
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la mise à jour');
      }

      const updatedAC = userData.map(u =>
        u.id === selectedUser.id
          ? { ...u, nom: formData.nom, prenom: formData.prenom, email: formData.email.toLowerCase(), telephone: formData.telephone || '', fonction: formData.fonction || '', dateMiseAJour: new Date().toLocaleDateString('fr-FR') }
          : u
      );
      setUserData(updatedAC);
      setFilteredData(updatedAC);
      setIsConfirmUpdateModalOpen(false);
      setIsEditModalOpen(false);

    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert(`Erreur: ${error instanceof Error ? error.message : 'Une erreur est survenue'}`);
    }
  };

  // Réinitialiser le mot de passe
  const handleResetPassword = (userToReset: PrestataireUser) => {
    setSelectedUser(userToReset);
    setIsResetPasswordModalOpen(true);
  };

  // Renvoyer l'invitation (Prestataire AC uniquement)
  const handleResendInvitation = (targetUser: PrestataireUser) => {
    setSelectedUser(targetUser);
    setIsResendInvitationModalOpen(true);
  };

  const handleConfirmResendInvitation = async () => {
    if (!selectedUser) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/gestionnaires/team/${selectedUser.idUser}/resend-invitation`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        }
      );
      const data = await response.json();
      if (!response.ok || data.status !== 'success') {
        throw new Error(data.message || 'Erreur lors du renvoi de l\'invitation');
      }
      alert(`Invitation renvoyée à ${selectedUser.email}`);
      setIsResendInvitationModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      alert(`Erreur: ${error instanceof Error ? error.message : 'Une erreur est survenue'}`);
    }
  };

  const handleConfirmResetPassword = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');

      // ===== PRESTATAIRE LIBRE =====
      if (isPrestataireLibre) {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/profile/team/${selectedUser.id}/reset-password`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Erreur lors de la réinitialisation du mot de passe');
        }

        alert(`Un email de réinitialisation a été envoyé à ${selectedUser.email}`);
        setIsResetPasswordModalOpen(false);
        return;
      }

      // ===== PRESTATAIRE ACCORD CADRE =====
      const response = await fetch(`${process.env.REACT_APP_API_URL}/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email: selectedUser.email.toLowerCase() })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la réinitialisation du mot de passe');
      }

      alert(`Un email de réinitialisation a été envoyé à ${selectedUser.email}`);
      setIsResetPasswordModalOpen(false);

    } catch (error) {
      console.error('Erreur:', error);
      alert(`Erreur: ${error instanceof Error ? error.message : 'Une erreur est survenue'}`);
    }
  };

  // Supprimer un utilisateur — ouvre DeleteMemberModal
  const handleOpenDeleteModal = (member: PrestataireUser) => {
    setMemberToDelete({
      id: Number(member.id),
      nom: member.nom,
      prenom: member.prenom,
      email: member.email,
    });
    setIsDeleteModalOpen(true);
  };

  const handleDeleteSuccess = (memberId: number) => {
    setIsDeleteModalOpen(false);
    setMemberToDelete(null);
    setUserData((prev) => prev.filter((m) => Number(m.id) !== memberId));
    setFilteredData((prev) => prev.filter((m) => Number(m.id) !== memberId));
  };

  // Recherche
  const handleSearch = (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setFilteredData(userData);
      return;
    }

    const searchTermLower = searchTerm.toLowerCase();
    const filtered = userData.filter(item =>
      item.nom.toLowerCase().includes(searchTermLower) ||
      item.prenom.toLowerCase().includes(searchTermLower) ||
      item.email.toLowerCase().includes(searchTermLower) ||
      item.telephone.toLowerCase().includes(searchTermLower)
    );

    setFilteredData(filtered);
  };

  // Body scroll lock when any modal is open
  const anyModalOpen = isCreateModalOpen || isEditModalOpen || isResetPasswordModalOpen || isResendInvitationModalOpen || isConfirmUpdateModalOpen || isSuccessModalOpen || isDeleteModalOpen;
  useEffect(() => {
    document.body.style.overflow = anyModalOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [anyModalOpen]);

  const columns = [
    {
      field: 'nom',
      header: 'Nom / Prénom',
      renderCell: (value: string, row: PrestataireUser) => (
        <div className="user-name">
          {row.prenom} {value}
        </div>
      )
    },
    {
      field: 'email',
      header: 'Email',
      renderCell: (value: string) => (
        <div className="user-email">
          {formatEmail(value)}
        </div>
      )
    },
    {
      field: 'telephone',
      header: 'Téléphone',
      renderCell: (value: string) => (
        <div className="user-phone">
          {formatPhone(value)}
        </div>
      )
    },
    {
      field: 'fonction',
      header: 'Fonction',
      renderCell: (value: string) => (
        <div className="user-fonction">
          {value || '-'}
        </div>
      )
    },
    {
      field: 'role',
      header: 'Rôle',
      renderCell: (value: string) => (
        <div className="user-role">
          {value}
        </div>
      )
    },
    {
      field: 'dateMiseAJour',
      header: 'Date de mise à jour',
      renderCell: (value: string) => (
        <div className="creation-date">
          {value}
        </div>
      )
    },
    {
      field: 'actions',
      header: 'Actions',
      renderCell: (value: any, row: PrestataireUser) => {
        // FRONT-12: Afficher les actions si l'utilisateur est admin (AC ou PL)
        const isUserAdmin = isAdminRole(user?.role || '');
        // Pour les PL: les admins peuvent gérer les membres simples
        // Pour les AC: les gestionnaires peuvent gérer les interlocuteurs
        const canManageRow = isAdminRole(user?.role || '') ? true : (row.role === 'Prestataire libre' || row.role === 'Prestataire');

        if (isUserAdmin && canManageRow) {
          return (
            <div className="actions-cell" style={{ display: 'flex', gap: '0.5rem' }}>
              <Button
                value="Modifier"
                variant="secondary"
                size="small"
                icon={<EditIcon />}
                onClick={() => handleOpenEditModal(row)}
              />
              <Button
                value="MDP"
                variant="secondary"
                size="small"
                icon={<KeyIcon />}
                onClick={() => handleResetPassword(row)}
              />
              {!isPrestataireLibre && (
                <Button
                  value="Renvoyer"
                  variant="secondary"
                  size="small"
                  onClick={() => handleResendInvitation(row)}
                />
              )}
              <Button
                value="Supprimer"
                variant="danger"
                size="small"
                onClick={() => handleOpenDeleteModal(row)}
              />
            </div>
          );
        } else {
          // Pour les autres cas, afficher un texte ou rien
          return (
            <div className="actions-cell" style={{ color: '#999', fontStyle: 'italic' }}>
              Non autorisé
            </div>
          );
        }
      }
    }
  ];

  if (error) {
    return <div className="error-message">Erreur: {error}</div>;
  }

  // FRONT-12: Vérifier si l'utilisateur peut créer des comptes (admin AC ou PL)
  const canCreateAccounts = isAdminRole(user?.role || '');

  return (
    <div className="gestion-comptes-prestataires">
      {/* Header avec barre de recherche et bouton créer */}
      <div className="header-actions" style={{ marginBottom: '3rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
        <div style={{ flex: '1' }}>
          <SearchBar
            placeholder="Rechercher un membre"
            onSearch={handleSearch}
          />
        </div>
        {canCreateAccounts ? (
          <Button
            value="Créer un utilisateur"
            variant="primary"
            size="small"
            icon={<PlusIcon />}
            onClick={handleOpenCreateModal}
          />
        ) : (
          <div title="Seul un administrateur peut créer de nouveaux utilisateurs" style={{ opacity: 0.5, cursor: 'not-allowed' }}>
            <Button
              value="Créer un utilisateur"
              variant="primary"
              size="small"
              icon={<PlusIcon />}
              disabled
            />
          </div>
        )}
      </div>

      {loading ? (
        <div className="membres-cards-grid">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="membre-card membre-card-skeleton">
              <div className="membre-card-inner">
                <span className="sk sk-badge" />
                <div className="membre-card-header">
                  <div className="membre-card-banner sk sk-banner" />
                  <div className="membre-card-avatar-wrapper">
                    <div className="sk sk-avatar" />
                  </div>
                </div>
                <div className="membre-card-body">
                  <div className="sk sk-line sk-line--name" />
                  <div className="sk sk-line sk-line--fonction" />
                  <div className="sk sk-line sk-line--contact" />
                  <div className="sk sk-line sk-line--contact" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (MEMBRES_VIEW_MODE as string) === 'table' ? (
        <TableauDeBord
          data={filteredData}
          columns={columns}
          onSearch={handleSearch}
          isSelectable={false}
          selectedItems={[]}
          onSelectionChange={() => { }}
          showActionsColumn={false}
          itemsPerPage={500}
        />
      ) : filteredData.length === 0 ? (
        <p className="membres-cards-empty">Aucun membre trouvé.</p>
      ) : (
        <div className="membres-cards-grid">
          {filteredData.map((member) => {
            const isAdminUser = isAdminRole(user?.role || '');
            const canManage = isAdminUser;
            const isEditable = isAdminUser;
            const initials = [member.prenom?.[0], member.nom?.[0]].filter(Boolean).join('').toUpperCase() || '?';
            return (
              <div
                key={member.id}
                className={`membre-card${isEditable ? ' membre-card--editable' : ''}${(member.role || '').toLowerCase().includes('signataire') ? ' membre-card--titulaire' : ' membre-card--libre'}`}
                onClick={() => { if (isEditable) handleOpenEditModal(member); }}
                role={isEditable ? 'button' : undefined}
                tabIndex={isEditable ? 0 : undefined}
                onKeyDown={isEditable ? (e) => { if (e.key === 'Enter') handleOpenEditModal(member); } : undefined}
              >
                <div className="membre-card-inner">
                  <span className="membre-card-role-badge">{member.role}</span>
                  <div className="membre-card-header">
                    <div className="membre-card-banner" />
                    <div className="membre-card-avatar-wrapper">
                      <span className="membre-card-avatar">{initials}</span>
                    </div>
                  </div>
                  <div className="membre-card-body">
                    <p className="membre-card-name">{member.prenom ? member.prenom.charAt(0).toUpperCase() + member.prenom.slice(1).toLowerCase() : ''} {member.nom.toUpperCase()}</p>
                    {member.fonction && <p className="membre-card-fonction">{member.fonction.charAt(0).toUpperCase() + member.fonction.slice(1).toLowerCase()}</p>}
                    <div className="membre-card-contacts">
                      {member.email && <span className="membre-card-contact membre-card-email">{formatEmail(member.email)}</span>}
                      {member.telephone && <span className="membre-card-contact">{formatPhone(member.telephone)}</span>}
                    </div>
                  </div>
                  <div className="membre-card-footer">
                    {member.dateMiseAJour && (
                      <span className="membre-card-date">Modifié le {member.dateMiseAJour}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de création */}
      {isCreateModalOpen && (
        <div className="modal-overlay" style={{ display: 'flex' }} onClick={() => setIsCreateModalOpen(false)}>
          <div className="modal-content" style={{ maxWidth: '600px', width: '90%' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Créer un utilisateur</h2>
              <button onClick={() => setIsCreateModalOpen(false)} className="close-button">
                <CrossIcon />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Prénom <span className="required-star">*</span></label>
                  <input
                    type="text"
                    value={formData.prenom}
                    onChange={(e) => {
                      setFormData({ ...formData, prenom: sanitizeTextInput(e.target.value) });
                      if (formErrors.prenom) setFormErrors(prev => ({ ...prev, prenom: '' }));
                    }}
                    className={formErrors.prenom ? 'input-error' : ''}
                    placeholder="Marie"
                    maxLength={100}
                  />
                  {formErrors.prenom && <span className="error-message">{formErrors.prenom}</span>}
                </div>
                <div className="form-group">
                  <label>Nom <span className="required-star">*</span></label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => {
                      setFormData({ ...formData, nom: sanitizeTextInput(e.target.value) });
                      if (formErrors.nom) setFormErrors(prev => ({ ...prev, nom: '' }));
                    }}
                    className={formErrors.nom ? 'input-error' : ''}
                    placeholder="Dupont"
                    maxLength={100}
                  />
                  {formErrors.nom && <span className="error-message">{formErrors.nom}</span>}
                </div>
              </div>
              <div className="form-group">
                <label>Email de connexion <span className="required-star">*</span></label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value });
                    if (formErrors.email) setFormErrors(prev => ({ ...prev, email: '' }));
                    if (formApiError) setFormApiError('');
                  }}
                  className={formErrors.email ? 'input-error' : ''}
                  placeholder="email@exemple.fr"
                />
                {formErrors.email && <span className="error-message">{formErrors.email}</span>}
              </div>
              <div className="form-group">
                <label>Téléphone <span className="required-star">*</span></label>
                <input
                  type="tel"
                  value={formData.telephone}
                  onChange={(e) => {
                    setFormData({ ...formData, telephone: e.target.value.replace(/\D/g, '').slice(0, 10) });
                    if (formErrors.telephone) setFormErrors(prev => ({ ...prev, telephone: '' }));
                  }}
                  className={formErrors.telephone ? 'input-error' : ''}
                  placeholder="0612345678"
                />
                {formErrors.telephone && <span className="error-message">{formErrors.telephone}</span>}
              </div>
              <div className="form-group">
                <label>Fonction</label>
                <input
                  type="text"
                  value={formData.fonction}
                  onChange={(e) => setFormData({ ...formData, fonction: e.target.value })}
                  placeholder="Ex: Chef de projet"
                />
              </div>
            </div>
            <span style={{ fontSize: '1rem', color: '#6b7280', margin: '0 2rem 1.2rem', display: 'block' }}>
              Un lien valable 60 minutes sera envoyé par email à l'utilisateur pour définir son mot de passe. Il devra également vérifier son adresse email. Ces deux actions sont accessibles depuis la page de connexion.
            </span>
            {formApiError && (
              <span className="error-message" style={{ margin: '0 2rem 1.2rem', display: 'block' }}>{formApiError}</span>
            )}
            <div className="modal-actions">
              <Button
                value="Annuler"
                variant="secondary"
                onClick={() => setIsCreateModalOpen(false)}
                disabled={isSubmitting}
              />
              <Button
                value={isSubmitting ? 'Création...' : 'Créer'}
                variant="primary"
                onClick={handleCreateUser}
                disabled={isSubmitting}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal d'édition */}
      {isEditModalOpen && selectedUser && (
        <EditUserModal
          isOpen={isEditModalOpen}
          onClose={() => { setIsEditModalOpen(false); setSelectedUser(null); }}
          onSave={handleSavePrestUser}
          userId={String(selectedUser.id)}
          userName={`${selectedUser.prenom} ${selectedUser.nom}`}
          userEmail={selectedUser.email}
          userPrenom={selectedUser.prenom}
          userFonction={selectedUser.fonction}
          userRole={selectedUser.role}
          userTelephone={selectedUser.telephone}
          userLoginEmail={selectedUser.loginEmail || ''}
          onSaveLoginEmail={handleSavePrestLoginEmail}
          onDeleteRequest={
            Number(selectedUser.idUser) !== Number(user?.id)
              ? () => { setIsEditModalOpen(false); handleOpenDeleteModal(selectedUser!); }
              : undefined
          }
          onResetPasswordRequest={() => { setIsEditModalOpen(false); handleResetPassword(selectedUser!); }}
          availableRoles={getAvailableRolesForUser(selectedUser)}
        />
      )}

      {/* Modal renvoi invitation (Prestataire AC) */}
      {isResendInvitationModalOpen && selectedUser && (
        <div className="modal-overlay" style={{ display: 'flex' }} onClick={() => setIsResendInvitationModalOpen(false)}>
          <div className="modal-content" style={{ maxWidth: '500px', width: '90%' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Renvoyer l'invitation</h2>
              <button onClick={() => setIsResendInvitationModalOpen(false)} className="close-button">
                <CrossIcon />
              </button>
            </div>
            <div className="modal-body">
              <p>
                Voulez-vous renvoyer l’email d’invitation à <strong>{selectedUser.email}</strong> ?
                Un nouveau lien de vérification lui sera envoyé.
              </p>
            </div>
            <div className="modal-actions">
              <Button
                value="Annuler"
                variant="secondary"
                size="small"
                onClick={() => setIsResendInvitationModalOpen(false)}
              />
              <Button
                value="Renvoyer"
                variant="primary"
                size="small"
                onClick={handleConfirmResendInvitation}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de réinitialisation de mot de passe */}
      {isResetPasswordModalOpen && selectedUser && (
        <div className="modal-overlay" style={{ display: 'flex' }} onClick={() => setIsResetPasswordModalOpen(false)}>
          <div className="modal-content" style={{ maxWidth: '500px', width: '90%' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Réinitialiser le mot de passe</h2>
              <button onClick={() => setIsResetPasswordModalOpen(false)} className="close-button">
                <CrossIcon />
              </button>
            </div>
            <div className="modal-body">
              <p>
                Voulez-vous envoyer un email de réinitialisation de mot de passe à <strong>{selectedUser.email}</strong> ?
              </p>
            </div>
            <div className="modal-actions">
              <Button
                value="Annuler"
                variant="secondary"
                size="small"
                onClick={() => setIsResetPasswordModalOpen(false)}
              />
              <Button
                value="Envoyer"
                variant="primary"
                size="small"
                onClick={handleConfirmResetPassword}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de mise à jour */}
      {isConfirmUpdateModalOpen && selectedUser && (
        <div className="modal-overlay" style={{ display: 'flex' }} onClick={() => setIsConfirmUpdateModalOpen(false)}>
          <div className="modal-content" style={{ maxWidth: '600px', width: '90%' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirmation de modification</h2>
              <button onClick={() => setIsConfirmUpdateModalOpen(false)} className="close-button">
                <CrossIcon />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '1rem', lineHeight: '1.6' }}>
                <strong>Êtes-vous sûr de vouloir modifier les informations de contact ?</strong>
              </p>
              <p style={{ color: '#666', lineHeight: '1.6' }}>
                Cette action affectera l'ensemble des coordonnées de contact des lots concernés pour cet interlocuteur.
              </p>
            </div>
            <div className="modal-actions">
              <Button
                value="Refuser"
                variant="secondary"
                onClick={() => setIsConfirmUpdateModalOpen(false)}
              />
              <Button
                value="Valider"
                variant="primary"
                onClick={handleUpdateUser}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de succès après création */}
      {isSuccessModalOpen && (
        <div className="modal-overlay" style={{ display: 'flex' }}>
          <div className="modal-content" style={{ maxWidth: '500px', width: '90%' }}>
            <div className="modal-header">
              <h2>Compte créé avec succès</h2>
            </div>
            <div className="modal-body">
              <p style={{ lineHeight: '1.8', fontSize: '1.5rem', color: '#333' }}>
                Un email a été envoyé à <strong>{createdUserEmail}</strong> avec un lien pour définir son mot de passe de connexion à Centralis.
              </p>
            </div>
            <div className="modal-actions">
              <Button
                value="Compris"
                variant="primary"
                onClick={() => setIsSuccessModalOpen(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de suppression membre */}
      {isDeleteModalOpen && memberToDelete && (
        <DeleteMemberModal
          member={memberToDelete}
          availableMembers={userData
            .filter((m) => Number(m.id) !== memberToDelete.id)
            .map((m) => ({ id: Number(m.id), nom: m.nom, prenom: m.prenom, email: m.email, role: m.role || '' }))
          }
          dependenciesEndpoint={`${process.env.REACT_APP_API_URL}/${isPrestataireLibre ? 'profile' : 'gestionnaires'}/team/${memberToDelete.id}/dependencies`}
          transferEndpoint={`${process.env.REACT_APP_API_URL}/${isPrestataireLibre ? 'profile' : 'gestionnaires'}/team/${memberToDelete.id}/transfer`}
          deleteEndpoint={`${process.env.REACT_APP_API_URL}/${isPrestataireLibre ? 'profile' : 'gestionnaires'}/team/${memberToDelete.id}`}
          onSuccess={handleDeleteSuccess}
          onClose={() => { setIsDeleteModalOpen(false); setMemberToDelete(null); }}
        />
      )}
    </div>
  );
};

export default GestionComptesPrestataires;
